/**
 * Central Testing-Mode REST Client
 *
 * Direct browser → Central calls (Bearer token). Paths/types from @levelup/central-auth.
 * Used only by platform-internal users when meta.features.testingMode === true.
 */

import {
  TESTING_MODE_PATHS,
  normalizeTestingModeOptions,
  normalizeTestingModeStatus,
} from '@levelup/central-auth';
import type {
  TestingModeOptions,
  TestingModeStatus,
  ApplyOverrideParams,
} from '@levelup/central-auth';
import { getAccessToken } from './centralAuth';

const RAW_CENTRAL_URL = import.meta.env.VITE_CENTRAL_URL;

function getCentralBase(): string | null {
  if (!RAW_CENTRAL_URL) return null;
  return RAW_CENTRAL_URL.replace(/\/+$/, '');
}

async function tmFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getAccessToken();
  if (!token) throw new Error('Not authenticated');
  const base = getCentralBase();
  if (!base) throw new Error('VITE_CENTRAL_URL not configured');

  const url = new URL(path, base).toString();
  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  if (!response.ok) throw new Error(`Central returned ${response.status}`);
  if (response.status === 204) return undefined as T;
  return response.json();
}

export async function fetchTestingModeOptions(): Promise<TestingModeOptions> {
  const raw = await tmFetch<unknown>(TESTING_MODE_PATHS.options);
  return normalizeTestingModeOptions(raw);
}

export async function fetchTestingModeStatus(): Promise<TestingModeStatus> {
  const raw = await tmFetch<unknown>(TESTING_MODE_PATHS.status);
  return normalizeTestingModeStatus(raw);
}

export async function applyTestingModeOverride(params: ApplyOverrideParams): Promise<void> {
  await tmFetch(TESTING_MODE_PATHS.override, {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export async function clearTestingModeOverride(): Promise<void> {
  await tmFetch(TESTING_MODE_PATHS.override, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Legacy in-app persona — local-only test shim.
// ---------------------------------------------------------------------------
// Central currently does not reflect the `legacy.persona:X` custom scope back
// in the switch-context response, so we persist the chosen persona locally
// for the duration of the testing-mode session. usePermissions() and
// useGetMenuQuery() read this to drive UX gating.
//
// The backend remains authoritative — this shim only affects frontend display.

const LEGACY_PERSONA_KEY = 'legacy.test-persona';

export function setLegacyTestPersona(persona: string | null): void {
  try {
    if (persona) localStorage.setItem(LEGACY_PERSONA_KEY, persona);
    else localStorage.removeItem(LEGACY_PERSONA_KEY);
  } catch {
    // localStorage may be unavailable (private mode, etc.) — ignore.
  }
}

export function getLegacyTestPersona(): string | null {
  try {
    return localStorage.getItem(LEGACY_PERSONA_KEY);
  } catch {
    return null;
  }
}
